import { getTargetAccountFiles } from "@/apis/v1/target-account";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { ITargetAccountFiles } from "./targetAccounts.interface";
import { filterDataAccordingToAllowedAdvertisers } from "../Advertiser/advertiser.validation";

/**
 * Every request parameter that changes the ANSWER is part of the key.
 *
 * It used to be just fileType and companyId, while `status`, `advertiserIds` and
 * `userCompanyId` travelled via `meta.queryParams` — so callers asking different
 * questions shared one cache entry and whichever fetched first decided what the
 * others saw. ABM and ContactBI both filter to Completed (ContactBI did not
 * until the target-account exclusion landed — an in-progress file made the
 * exclusion silently partial); Users → Edit user narrows to one advertiser and
 * ABM does not. Visiting one page then the other showed the wrong list in
 * both directions.
 *
 * Fixed length, with explicit nulls rather than an argument-dependent shape. An
 * earlier version appended `status` only when it was present, which quietly
 * orphaned `Report/ReportSetup/create/Settings.tsx` — it reads this cache
 * directly with `getQueryData`, and the shorter key it built stopped matching
 * anything any producer emitted.
 */
export const TARGET_ACCOUNT_FILE_KEY = (
  fileType: "domains" | "emails" | "all",
  companyId?: number,
  status?: string,
  advertiserIds?: number[] | string,
  userCompanyId?: number
) => [
  "target-account",
  "files",
  fileType,
  companyId ?? null,
  status ?? null,
  // Sorted, because TanStack hashes arrays POSITIONALLY: selecting advertisers
  // 1 then 3, then dropping and re-adding 1, gives [3,1] and mints a second
  // cache entry for an answer the server considers identical — the picker
  // empties and refetches on a reorder. The ABM payload sorts its id arrays for
  // the same reason.
  Array.isArray(advertiserIds)
    ? [...advertiserIds].sort((a, b) => a - b)
    : advertiserIds ?? null,
  userCompanyId ?? null,
];

export function useTargetAccountFiles({
  queryParams,
  enabled = true,
  keepOwnCompanyId = false,
}: {
  queryParams: ITargetAccountFiles;
  enabled?: boolean;
  /**
   * Keep files owned by the user's OWN company alongside their permitted
   * advertisers'. Off by default so existing callers are unaffected.
   *
   * Needed wherever the backend allows an agency to use its own lists — which
   * `getFiles` does, and which the advertiser-subset filter would otherwise
   * strip client-side, leaving a restricted agency user unable to pick a list
   * the API would have accepted.
   */
  keepOwnCompanyId?: boolean;
}) {
  /**
   * Filters a COPY, and is stable across renders. Both matter.
   *
   * `filterDataAccordingToAllowedAdvertisers` assigns its result back onto
   * `data.rows`, which mutates the object react-query is holding — so a caller
   * that filters more aggressively would permanently strip rows from the shared
   * cache entry and every other consumer would then see the narrowed list. Now
   * that `keepOwnCompanyId` makes two callers filter differently, that is
   * reachable: one page would silently empty another page's picker.
   *
   * The identity has to be stable too. `select` re-runs whenever its own
   * identity changes, so an inline factory call rebuilt the result on every
   * render; returning a fresh object each time would then hand consumers a new
   * `data` reference every render.
   */
  const select = React.useCallback(
    (data: any) =>
      filterDataAccordingToAllowedAdvertisers(
        "companyId",
        keepOwnCompanyId
      )(Array.isArray(data) ? data : { ...data }),
    [keepOwnCompanyId]
  );

  return useQuery(
    TARGET_ACCOUNT_FILE_KEY(
      queryParams.fileType,
      queryParams.companyId,
      queryParams.status,
      queryParams.advertiserIds,
      queryParams.userCompanyId
    ),
    getTargetAccountFiles,
    {
      enabled,
      meta: {
        queryParams,
      },
      select,
    }
  );
}
